import { Checkbox, Paper, TableContainer } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import * as monitoredCycleTimeApi from '../../api/app/monitoredCycleTimeApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { DataTable } from '../../components/core/DataTable/DataTable';
import { HeaderCell } from '../../components/core/DataTable/dataTable.types';
import { Loader } from '../../components/core/Loader/Loader';
import { useAuth } from '../../contexts/authWindows';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { MonitoredCycleTime } from '../../types/app/monitoredCycleTime.types';
import { UserRoles } from '../../types/core/auth.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
interface Props {
  parentId: number;
}

export function MonitoredCycleTimeList({ parentId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isReadOnly = user?.roles.includes(UserRoles.Admin) ? false : true;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const { data, isLoading, isError } = useQuery<MonitoredCycleTime[]>('monitoredCycleTimes', () =>
    monitoredCycleTimeApi.getMonitoredCycleTimesByDep(parentId),
  );
  const { mutate: mutateDeleteMonitoredCycleTime } = useMutation(
    monitoredCycleTimeApi.deleteMonitoredCycleTime,
  );

  const headerCells: HeaderCell[] = [
    {
      title: 'Main',
      key: 'main',
      cellRenderer: (e) => {
        return <Checkbox disabled checked={e.main} />;
      },
    },
    { title: 'OPC Client', key: 'opcClientInstanceName' },
    { title: 'Name', key: 'name' },
    { title: 'Influx value name', key: 'influxValueName' },
    { title: 'Node id', key: 'nodeId' },
    { title: 'Updated', key: 'updated', dataType: 'Date' },
  ];
  /*   const headerButtons = [
    <Fab
      key={0}
      color="primary"
      aria-label="add"
      size="medium"
      onClick={() => {
        navigate(`${paths.ASSETS_LIST}/${parentId}${paths.MONITORED_CYCLE_TIME_CREATE}`, {
          state: { parentId },
        });
      }}
    >
      <AddIcon />
    </Fab>,
  ]; */

  const handleOnCreate = () =>
    navigate(`${paths.ASSETS_LIST}/${parentId}${paths.MONITORED_CYCLE_TIME_CREATE}`, {
      state: { parentId },
    });

  const handleOnEdit = (id: number) =>
    navigate(`${paths.ASSETS_LIST}/${parentId}${paths.MONITORED_CYCLE_TIME_DETAIL}/${id}`);
  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeleteMonitoredCycleTime(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(dictionary.MONITORED_COUNTER_DELETED, { variant: 'success' });
        queryClient.invalidateQueries(['monitoredCycleTimes']);
        queryClient.invalidateQueries(['assetTreeView']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data) return <Loader />;
  return (
    <>
      {/*       <PageHeader
        title={'Monitored cycle time'}
        buttons={isReadOnly ? null : headerButtons}
        headlineVariant={'h5'}
      /> */}
      <TableContainer component={Paper}>
        <DataTable
          headerCells={headerCells}
          items={data}
          onDeleteClick={handleOnDelete}
          onCreateClick={handleOnCreate}
          onEditClick={isReadOnly ? undefined : handleOnEdit}
          isReadOnly={isReadOnly}
        />
      </TableContainer>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
